import React from 'react';
import { Spinner } from 'react-bootstrap';
import { ListColumn, FooterOptions } from '../Datalist/Datalist'; // Import your Datalist component
import Datalist from '../Datalist/Datalist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import './SupplierTable.scss';
import { format } from 'date-fns'; // Import date-fns for formatting
import { useNavigate } from 'react-router-dom';

interface Supplier {
    supplierName: string;
    orgNumber: string;
    aValue: string | number; // Ensure it can be string or number
    period: string; // Change this to Period to reflect the API response
    thresholdYellow?: number | null; // Optional field for yellow threshold (number or null)
    thresholdRed?: number | null;    // Optional field for red threshold (number or null)
}

interface SupplierTableProps {
    supplierData: Supplier[];
    isLoading: boolean;
    selectedDate: Date | null;
    filter: string;
    searchText: string;
}

const SupplierTable: React.FC<SupplierTableProps> = ({ supplierData, isLoading, selectedDate, filter, searchText }) => {
    // Format selected date to match the 'Period' format "yyyy-MM"
    const formattedSelectedDate = selectedDate ? format(selectedDate, 'yyyy-MM') : null;

    const navigate = useNavigate(); // Use the navigate hook for navigation

    // Function to handle row click
    const handleRowClick = (supplier: Supplier) => {
        // Navigate to the ReviewSupplierPage using orgNumber and period
        navigate(`/reviewsupplier/${supplier.orgNumber}`, {
            state: {
                orgNumber: supplier.orgNumber, // Only send orgNumber
                period: supplier.period ?? formattedSelectedDate // Send period (date) for fetching relevant data
            },
        });
    };
    
    // Filter supplier data based on selected date (now Period), filter, and search text
    const filteredData = supplierData.filter(supplier => {
        // Allow suppliers with null period to pass the date filter, and ensure suppliers with matching period are included
        const matchesDate = formattedSelectedDate ? (supplier.period === formattedSelectedDate || supplier.period === null) : true;

        const aValue = typeof supplier.aValue === 'string' ? parseFloat(supplier.aValue.replace('%', '')) : supplier.aValue;

        let color = '#07A235';
        let icon = faCheckCircle;

        // Only show the icon if both thresholds exist
        let hasThresholds = supplier.thresholdRed !== null && supplier.thresholdYellow !== null;
        if (hasThresholds) {
            if (supplier.thresholdRed !== null && supplier.thresholdRed !== undefined && aValue > supplier.thresholdRed) {
                color = '#DC143C'; // Red if above red threshold
                icon = faTimesCircle;
            } else if (supplier.thresholdYellow !== null && supplier.thresholdYellow !== undefined && aValue > supplier.thresholdYellow) {
                color = '#F5A32D'; // Yellow if above yellow threshold
                icon = faExclamationCircle;
            }
        }

        const matchesFilter = filter ? (
            (filter === 'green' && color === '#07A235') ||
            (filter === 'red' && color === '#DC143C') ||
            (filter === 'yellow' && color === '#F5A32D')
        ) : true;

        // Exclude suppliers with no thresholds when a specific filter is selected
        if (filter && !hasThresholds) {
            return false;
        }

        const matchesSearch = supplier.supplierName.toLowerCase().includes(searchText.toLowerCase()) ||
            supplier.orgNumber.includes(searchText) ||
            String(supplier.aValue).includes(searchText);

        return matchesDate && matchesFilter && matchesSearch;
    });



    // Define the columns for the Supplier table
    const columns: ListColumn<Supplier>[] = React.useMemo(() => [
        {
            key: 'supplierName',
            fieldName: 'supplierName',
            name: 'Leverantörsnamn',
            maxWidth: '80%',
            minWidth: 100,
            isActive: true,
            sorting: true, // Enable sorting
            onRender: (item: Supplier) => item.supplierName,
            footer: (items: Supplier[]) => `Totalt antal rader: ${items.length}`,
        },
        {
            key: 'orgNumber',
            fieldName: 'orgNumber',
            name: 'Organisationsnummer',
            maxWidth: '15%',
            minWidth: 100,
            isActive: true,
            sorting: true, // Enable sorting
            onRender: (item: Supplier) => item.orgNumber,
        },
        {
            key: 'aValue',
            fieldName: 'aValue',
            name: 'A.Värde',
            maxWidth: '5%',
            minWidth: 50,
            isActive: true,
            sorting: true, // Enable sorting
            onRender: (item: Supplier) => {
                const aValue = typeof item.aValue === 'string' ? parseFloat(item.aValue.replace('%', '')) : item.aValue;

                // Check if thresholds are explicitly defined (allow 0 as valid)
                const hasRedThreshold = item.thresholdRed !== null && item.thresholdRed !== undefined;
                const hasYellowThreshold = item.thresholdYellow !== null && item.thresholdYellow !== undefined;

                let color = '#07A235'; // Default to green
                let icon = faCheckCircle;

                // Prioritize red if value exceeds or equals red threshold
                if (hasRedThreshold && aValue >= item.thresholdRed!) {
                    color = '#DC143C'; // Red
                    icon = faTimesCircle;
                }
                // Else, check for yellow threshold
                else if (hasYellowThreshold && aValue >= item.thresholdYellow!) {
                    color = '#F5A32D'; // Yellow
                    icon = faExclamationCircle;
                }

                // Render the value with the appropriate icon
                return (
                    <div className="a-value-container">
                        <span className="a-value-text">{aValue !== null && aValue !== undefined ? `${aValue.toFixed(2)}%` : "N/A"}</span>
                        {aValue !== null && aValue !== undefined && (
                            <FontAwesomeIcon icon={icon} className="a-value-icon" style={{ color }} />
                        )}
                    </div>
                );
            },
            footer: (items: Supplier[]) => {
                if (items.length === 0) return 'Avg: N/A';
                const total = items.reduce((sum, row) => {
                    const value = typeof row.aValue === 'string' ? parseFloat(row.aValue.replace('%', '')) : row.aValue;
                    return sum + (value || 0);
                }, 0);
                const avg = (total / items.length).toFixed(2);
                return `Avg: ${avg}%`;
            },
        },
    ], [supplierData, filter, searchText, selectedDate]);

    return (
        <div className="supplier-table-wrapper" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {/* Use the Datalist component */}
            <Datalist
                items={filteredData}
                isLoading={isLoading}
                columns={columns}
                onClick={(item: Supplier) => handleRowClick(item)} // Pass the full supplier object
                setColumns={(updatedColumns: ListColumn<Supplier>[]) => {
                    // console.log('Columns updated:', updatedColumns);
                }}
                initialSortKey="aValue"  // Set default sort column
                descendingSort={false}    // Set default sort order to descending
                noItemsText="Inga leverantörer att visa"
                uniqueKey="orgNumber"  // Use 'orgNumber' as the unique key
            />
        </div>
    );
};

export default SupplierTable;
